import React, {useRef} from 'react';
import {nanoid} from 'nanoid';
import {
	ListItem,
	ListItemButton,
	type ListItemProps,
	ListItemText,
	listItemButtonClasses,
	type ListItemButtonProps,
} from '@mui/material';
import {splitEventHandler} from '@netx/core-next/utils';
import {styled} from '@netx/core-components/Theme';
import {Switch, type SwitchProps} from '@netx/core-components/Switch';
import useSharedRef from '@netx/core-components/hooks/useSharedRef';

const SwitchListItemRoot = styled(ListItem)(({theme}) => ({
	// need to make more room for the switch
	[`& > .${listItemButtonClasses.root}`]: {
		paddingRight: 70,
	},
}));

export interface SwitchListItemProps extends SwitchProps {
	description?: string;
	label: string;
	ListItemProps?: Partial<Omit<ListItemProps, 'secondaryAction'>>;
	ListItemButtonProps?: Partial<ListItemButtonProps>;
}

export const SwitchListItemPrimitive = (
	{
		classes,
		description,
		inputRef,
		label,
		ListItemProps,
		ListItemButtonProps,
		...props
	}: SwitchListItemProps,
	ref: any,
) => {
	const sharedInputRef = useSharedRef(null, [inputRef]);
	const uuid = useRef(nanoid());

	return (
		<SwitchListItemRoot
			ref={ref}
			disablePadding
			{...ListItemProps}
			secondaryAction={
				<Switch
					{...props}
					edge="end"
					inputProps={{
						'aria-labelledby': `${uuid.current}-label`,
						...props.inputProps,
					}}
					inputRef={sharedInputRef}
					onClick={(event) => {
						// prevent the click event from bubbling up to the list item
						event.stopPropagation();
						props.onClick?.(event);
					}}
				/>
			}
		>
			<ListItemButton
				disableRipple
				{...ListItemButtonProps}
				onClick={splitEventHandler(ListItemButtonProps?.onClick, () => {
					// pipe the click event to the input
					(sharedInputRef.current as unknown as HTMLElement)?.click();
				})}
				style={{
					cursor: props.disabled ? 'not-allowed' : undefined,
					...ListItemButtonProps?.style,
				}}
			>
				<ListItemText
					id={`${uuid.current}-label`}
					primary={label}
					secondary={description}
				/>
			</ListItemButton>
		</SwitchListItemRoot>
	);
};

/**
 * Combines the functionality of a ListItem with a Switch.
 */
export const SwitchListItem = React.forwardRef(SwitchListItemPrimitive) as (
	props: SwitchListItemProps,
) => JSX.Element;

export default SwitchListItem;
